import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface TrialBalanceFilters {
  subsidiary_id: number | string;
  fiscal_period_id: number | string; // Required by backend
  parent_company_id?: number | string; // For consolidated reports (future feature)
  limit?: number;
  offset?: number;
  search?: string;
}

export interface TrialBalanceAccount {
  account_id: number;
  account_code: string;
  account_name: string;
  account_type: string;
  beginning_debit: number;
  beginning_credit: number;
  period_debit: number;
  period_credit: number;
  ending_debit: number;
  ending_credit: number;
  net_balance: number;
}

export interface TrialBalanceApiResponse {
  status: string;
  message: string;
  data: {
    subsidiary_id: number;
    fiscal_period_id: number;
    accounts: TrialBalanceAccount[];
    total_beginning_debit: number;
    total_beginning_credit: number;
    total_period_debit: number;
    total_period_credit: number;
    total_ending_debit: number;
    total_ending_credit: number;
    is_balanced: boolean;
  };
}

@Injectable({
  providedIn: 'root'
})
export class TrialBalanceService {
  private readonly API_BASE_URL = environment.accountingUrl;
  private http = inject(HttpClient);

  /**
   * Get trial balance with optional filters
   * Endpoint: GET /api/v1/financial-statements/trial-balance
   * 
   * Required: subsidiary_id and fiscal_period_id
   */
  getTrialBalance(filters: TrialBalanceFilters): Observable<TrialBalanceApiResponse> {
    let params = new HttpParams();

    // Required: subsidiary_id
    if (filters.subsidiary_id !== undefined && filters.subsidiary_id !== null) {
      params = params.set('subsidiary_id', filters.subsidiary_id.toString());
    }

    // Required: fiscal_period_id
    if (filters.fiscal_period_id !== undefined && filters.fiscal_period_id !== null) {
      params = params.set('fiscal_period_id', filters.fiscal_period_id.toString());
    }

    // Optional: parent_company_id for consolidated reports (future feature)
    if (filters.parent_company_id !== undefined && filters.parent_company_id !== null) {
      params = params.set('parent_company_id', filters.parent_company_id.toString());
    }

    if (filters.limit !== undefined) {
      params = params.set('limit', filters.limit.toString());
    }

    if (filters.offset !== undefined) {
      params = params.set('offset', filters.offset.toString());
    }

    if (filters.search) {
      params = params.set('search', filters.search);
    }

    return this.http.get<TrialBalanceApiResponse>(`${this.API_BASE_URL}/api/v1/financial-statements/trial-balance`, {
      params,
      withCredentials: true
    });
  }
}

